import { setSelectedCharacterStyles, setSelectedParagraphs } from "src/features/review-slice";
import {
  findCharacterSelectionTarget,
  isSelectionHeld,
  resetCharacterFiltersForSelection,
  resetParagraphFiltersForSelection,
  saveReviewProgress,
  selectParagraphInDocument,
  selectTextInDocument,
} from "src/services";
import { selectSelectedIndices } from "src/features/selectors";
import { store } from "src/store";

type IsSuperseded = () => boolean;

async function handleCharacterSelection(isSuperseded: IsSuperseded) {
  return await Word.run(async (context) => {
    const range = context.document.getSelection();
    const paragraphs = range.paragraphs;
    paragraphs.load("items/uniqueLocalId");
    const title = await findCharacterSelectionTarget(context, range);
    if (isSuperseded()) return;

    store.dispatch(setSelectedParagraphs(paragraphs.items.map((p) => p.uniqueLocalId)));

    await resetCharacterFiltersForSelection(title);
    store.dispatch(setSelectedCharacterStyles(title));
    if (isSuperseded()) return;
    if (title !== null && range.isEmpty) {
      await selectTextInDocument(title);
    }
    await saveReviewProgress({ selectedCharacterStyleId: title });
  });
}

async function handleParagraphSelection(isSuperseded: IsSuperseded) {
  return await Word.run(async (context) => {
    const range = context.document.getSelection();
    const paragraphs = range.paragraphs;
    range.load("isEmpty");
    paragraphs.load("items/uniqueLocalId, items/style");
    await context.sync();
    if (isSuperseded()) return;

    if (paragraphs.items.length >= 1) {
      await resetParagraphFiltersForSelection(paragraphs.items[0]);
    }

    const selection = paragraphs.items.map((p) => p.uniqueLocalId);
    store.dispatch(setSelectedParagraphs(selection));

    const state = store.getState();
    const selectedIndices = selectSelectedIndices(state);
    if (selectedIndices.length > 0) {
      await saveReviewProgress({
        selectedIndices: [selectedIndices[0], selectedIndices[selectedIndices.length - 1]],
        total: state.review.paragraphs?.length ?? 0,
      });
    }

    // Only a bare cursor snaps; text the user selected is left as-is.
    if (selection.length === 1 && range.isEmpty && !isSuperseded()) {
      await selectParagraphInDocument(selection[0]);
    }
  });
}

async function handleSelectionChanged(isSuperseded: IsSuperseded) {
  // Our own selects and edits raise this event too; only a real move should be handled.
  if (isSuperseded() || (await isSelectionHeld())) return;

  const { outlineMode } = store.getState().review;
  if (outlineMode === "character") {
    await handleCharacterSelection(isSuperseded);
  } else if (outlineMode === "chapter" || outlineMode === "style") {
    await handleParagraphSelection(isSuperseded);
  }
}

let latestSelectionEvent = 0;
let selectionQueue: Promise<void> = Promise.resolve();
let navigationDepth = 0;

// Events that arrive after `fn` resolves are still filtered by the selection hold.
export async function withoutSelectionHandling<T>(fn: () => Promise<T>): Promise<T> {
  navigationDepth++;
  latestSelectionEvent++;
  try {
    return await fn();
  } finally {
    navigationDepth--;
  }
}

// Serialized so an earlier event can't finish after a later one and move the cursor back.
export function onSelectionChanged() {
  if (navigationDepth > 0) return selectionQueue;
  const event = ++latestSelectionEvent;
  const isSuperseded = () => event !== latestSelectionEvent;

  selectionQueue = selectionQueue
    .then(() => handleSelectionChanged(isSuperseded))
    .catch((e) => console.error(e));
  return selectionQueue;
}
